import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, useCategoryLabel, CHART_COLORS } from "@/components/common";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Users, Activity, GraduationCap, AlertTriangle, ArrowRight } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="flex items-center gap-4">
      <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
        <Icon className="h-6 w-6" style={{ color: accent }} />
      </div>
      <div><div className="text-2xl font-head font-bold text-slate-900">{value}</div><div className="text-sm text-slate-500">{label}</div></div>
    </Card>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const catLabel = useCategoryLabel();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/admin").then((r) => setData(r.data)); }, []);

  const dist = (data?.category_distribution || []).map((c) => ({ name: catLabel(c.category_code), count: c.count }));
  const trend = data?.trend || [];

  return (
    <div>
      <PageHeader title={t("adminDashboard")} subtitle="Training program overview"
        action={<button onClick={() => navigate("/admin/analytics")} className="text-sm text-blue-600 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="goto-admin-analytics">{t("analytics")} <ArrowRight className="h-4 w-4" /></button>} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Stat icon={Users} label={t("users")} value={data?.total_users ?? 0} accent="#2563EB" />
        <Stat icon={GraduationCap} label="Trainees" value={data?.trainees ?? 0} accent="#059669" />
        <Stat icon={Activity} label={t("totalSessions")} value={data?.total_sessions ?? 0} accent="#D97706" />
        <Stat icon={AlertTriangle} label={t("topMistakes")} value={data?.top_mistakes?.length ?? 0} accent="#DC2626" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("categoryDistribution")}</h3>
          {dist.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dist} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("trend")}</h3>
          {trend.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
      </div>
    </div>
  );
}
